import React, { useEffect, useState, useContext, useRef } from "react";
import Seo from "../../../shared/ui/Seo";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { S3_URL_PRODUCTS } from "../../../shared/utils/constants";
import { getProductsByListOfIds } from "../../products/api";
import { AuthContext } from "../../auth/context/AuthContext";
import { toast } from "react-toastify";
import { getCoupondetails, getAvailableCoupons } from "../../coupons/api";
import Loading from "../../../shared/ui/Loading";
import NoResults from "../../../shared/ui/NoResults";
import { parseWeight } from "../../checkout/utils/deliveryFee";
import useDeliveryFee from "../../checkout/hooks/useDeliveryFee";
import DeliveryFeeAmount from "../../checkout/components/DeliveryFeeAmount";
import CheckoutSteps from "../../checkout/components/CheckoutSteps";
import { CouponField, CouponTicket } from "../../coupons/components/CouponTicket";
import { getEffectivePrice, formatPrice } from "../../checkout/utils/pricing";
import "../../../shared/styles/cart-checkout.css";

const CartSection = () => {
  const { cart, removeFromCart, clearCartItems, updateQuantity } = useCart();
  const [products, setProducts] = useState([]);
  const { isAuthenticated } = useContext(AuthContext);
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  // Ref to hold debouncing timers for each product
  const updateTimeouts = useRef({});


  const fetchAvailableCoupons = async () => {
    try {
      const data = await getAvailableCoupons();
      setAvailableCoupons(data);
    } catch (error) {
      console.error("Error fetching available coupons:", error);
    }
  };

  // Cart changes (quantity, removal) apply in place; product details are only
  // fetched for items not shown yet, so the page never flips to a loader.
  const productsRef = useRef(products);
  productsRef.current = products;

  useEffect(() => {
    const cartIdOf = (item) => (isAuthenticated ? item.id.productId : item.id);
    const quantities = new Map(cart.map((item) => [cartIdOf(item), item.quantity]));

    // A quantity still waiting on its debounced update keeps its local value.
    setProducts((prev) =>
      prev
        .filter((product) => quantities.has(product.id))
        .map((product) =>
          updateTimeouts.current[product.id]
            ? product
            : { ...product, quantity: quantities.get(product.id) }
        )
    );

    const shown = new Set(productsRef.current.map((product) => product.id));
    const missing = cart.filter((item) => !shown.has(cartIdOf(item)));
    if (missing.length === 0) return;

    let cancelled = false;
    const fetchMissing = async () => {
      if (shown.size === 0) setLoading(true);
      try {
        const response = await getProductsByListOfIds(missing.map(cartIdOf));
        const productsFromApi = Array.isArray(response) ? response : response?.products || [];
        const fetched = missing
          .map((item) => {
            const productWrapper = productsFromApi.find(
              (p) => p.product.id === cartIdOf(item)
            );
            if (!productWrapper) return null;
            const product = productWrapper.product;
            return {
              id: product.id,
              name: product.name,
              images: product.images,
              salePrice: product.salePrice,
              regularPrice: product.regularPrice,
              stockQuantity: product.stockQuantity,
              quantity: item.quantity,
              sku: product.sku,
              configuration: product.configuration || null,
              nonCod: product.nonCod || false,
              slug: product.slug,
              weight: parseWeight(product.weight),
            };
          })
          .filter(Boolean);
        if (cancelled) return;
        // Keep the cart's order.
        setProducts((prev) => {
          const byId = new Map([...prev, ...fetched].map((product) => [product.id, product]));
          return cart.map((item) => byId.get(cartIdOf(item))).filter(Boolean);
        });
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMissing();
    return () => {
      cancelled = true;
    };
  }, [cart, isAuthenticated]);

  useEffect(() => {
    if (localStorage.getItem("couponDetails")) {
      setCouponDetails(JSON.parse(localStorage.getItem("couponDetails")));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && cart.length > 0) {
      fetchAvailableCoupons();
    }
  }, [isAuthenticated, cart]);

  const {
    deliveryFee,
    deliveryFeeStatus,
    loadDeliveryFee,
  } = useDeliveryFee(
    products.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      weight: item.weight,
    }))
  );
  const cartTotal = products
    .reduce((total, item) => total + item.quantity * getEffectivePrice(item), 0)
    .toFixed(2);

  // Debounced handler to update the quantity with an optimistic UI update.
  const debouncedHandleUpdateQuantity = (productId, newQuantity) => {
    // Update the local products state immediately.
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId
          ? { ...product, quantity: newQuantity }
          : product
      )
    );

    // Clear any previous timeout for this product.
    if (updateTimeouts.current[productId]) {
      clearTimeout(updateTimeouts.current[productId]);
    }

    // Debounce the update call by 500ms.
    updateTimeouts.current[productId] = setTimeout(async () => {
      delete updateTimeouts.current[productId];
      try {
        // Using the unified updateQuantity from the cart context
        await updateQuantity(productId, newQuantity);
      } catch (error) {
        console.error("Error updating cart:", error);
        toast.error("Failed to update cart");
      }
    }, 500);
  };

  // Remove a single item from the cart.
  const handleRemoveFromCart = async (productId) => {
    try {
      await removeFromCart(productId);
    } catch (error) {
      console.error("Error removing item from cart:", error);
      toast.error("Failed to remove item from cart");
    }
  };

  // Clear the entire cart.
  const handleDeleteCart = async () => {
    if (!window.confirm("Are you sure you want to clear your cart?")) {
      return; // Exit if the user cancels
    }
    try {
      await clearCartItems();
      localStorage.removeItem("couponDetails");
      setCouponDetails(null);
      setAvailableCoupons([]);
    } catch (error) {
      console.error("Error deleting cart:", error);
      toast.error("Failed to delete cart");
    }
  };

  const handleInputChange = (e) => {
    setCouponCode(e.target.value);
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = await getCoupondetails(couponCode);
      setCouponDetails(data);
      localStorage.setItem("couponDetails", JSON.stringify(data));
      toast.success("Coupon applied successfully");
    } catch (error) {
      console.error("Error applying coupon:", error);
      const errorMessage = error.response.data.error;
      toast.error(errorMessage || "Failed to apply coupon");
    }
  };

  const handleRemoveCoupon = () => {
    setCouponDetails(null);
    setCouponCode("");
    localStorage.removeItem("couponDetails");
  };

  let discountAmount = 0;
  let discountedTotal = Number(cartTotal) || 0;
  if (couponDetails) {
    if (couponDetails.couponType === "PERCENTAGE") {
      discountAmount = (discountedTotal * couponDetails.discountedValue) / 100;
    } else {
      discountAmount = Number(couponDetails.discountedValue) || 0;
    }
    discountedTotal -= discountAmount;
  }

  // Calculate the final total including delivery fee
  const finalTotal = discountedTotal + (deliveryFee ?? 0);

  if (loading && products.length === 0) {
    return <Loading message="Loading cart…" />;
  }
  if (cart.length === 0) {
    return (
      <NoResults message="Your cart is empty. Start shopping now..." />
    );
  }

  const itemCount = products.reduce((sum, item) => sum + item.quantity, 0);
  // The applied coupon is already shown as a ticket above the list.
  const otherCoupons = availableCoupons.filter(
    (coupon) => coupon.couponId !== couponDetails?.couponId
  );

  return (
    <section className="bm-shop">
      <Seo title="Cart" noindex />
      <div className="bm-head">
        <h1 className="bm-title">
          Your Cart
          <span className="bm-count">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </span>
        </h1>
        <CheckoutSteps current={0} />
      </div>

      <div className="bm-grid">
        <div className="bm-stack">
          <div className="bm-card">
            <div className="bm-card__head">
              <h2 className="bm-card__title">
                Items
              </h2>
              <button
                type="button"
                className="bm-link bm-link--danger"
                onClick={handleDeleteCart}
              >
                <i className="ph ph-trash-simple" />
                Clear cart
              </button>
            </div>

            <div className="bm-items">
              {products.map((item) => {
                const effectivePrice = getEffectivePrice(item);
                return (
                  <div className="bm-item" key={item.id}>
                    <Link
                      to={`/product-details/${item.slug}`}
                      className="bm-thumb"
                    >
                      <img
                        src={`${S3_URL_PRODUCTS}${item.images[0]}`}
                        alt={item.name}
                      />
                    </Link>
                    <div className="bm-item__info">
                      <Link
                        to={`/product-details/${item.slug}`}
                        className="bm-item__name"
                      >
                        {item.name}
                      </Link>
                      <span className="bm-item__unit">
                        LKR {formatPrice(effectivePrice)} each
                      </span>
                      {item.nonCod && (
                        <div>
                          <span className="bm-tag">
                            <i className="ph-fill ph-info" />
                            COD not available
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="bm-qty">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        onClick={() =>
                          debouncedHandleUpdateQuantity(
                            item.id,
                            item.quantity - 1
                          )
                        }
                        disabled={item.quantity <= 1}
                      >
                        <i className="ph-bold ph-minus" />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() =>
                          debouncedHandleUpdateQuantity(
                            item.id,
                            item.quantity + 1
                          )
                        }
                      >
                        <i className="ph-bold ph-plus" />
                      </button>
                    </div>
                    <span className="bm-item__total">
                      LKR {formatPrice(item.quantity * effectivePrice)}
                    </span>
                    <button
                      type="button"
                      className="bm-remove"
                      aria-label={`Remove ${item.name}`}
                      onClick={() => handleRemoveFromCart(item.id)}
                    >
                      <i className="ph ph-trash" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {isAuthenticated && (
            <div className="bm-card">
              <div className="bm-card__head">
                <h2 className="bm-card__title">
                  Coupons
                </h2>
              </div>
              {couponDetails === null ? (
                <CouponField
                  value={couponCode}
                  onChange={handleInputChange}
                  onApply={handleCouponSubmit}
                  applyDisabled={couponCode === ""}
                />
              ) : (
                <CouponTicket
                  coupon={couponDetails}
                  onRemove={handleRemoveCoupon}
                />
              )}

              {otherCoupons.length > 0 && (
                <>
                  <p className="bm-cticket-list__title">Available coupons</p>
                  <div className="bm-cticket-list">
                    {otherCoupons.map((coupon) => (
                      <CouponTicket
                        key={coupon.couponId}
                        coupon={coupon}
                        onSelect={() => {
                          setCouponDetails(coupon);
                          localStorage.setItem(
                            "couponDetails",
                            JSON.stringify(coupon)
                          );
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <aside className="bm-card bm-summary">
          <div className="bm-card__head">
            <h2 className="bm-card__title">
              Order Summary
            </h2>
          </div>
          <div className="bm-lines">
            <div className="bm-line">
              <span>Subtotal</span>
              <span>LKR {formatPrice(cartTotal)}</span>
            </div>
            {couponDetails && (
              <div className="bm-line bm-line--discount">
                <span>Discount ({couponDetails.couponCode})</span>
                <span>-LKR {formatPrice(discountAmount)}</span>
              </div>
            )}
            <div className="bm-line">
              <span>Delivery Fee</span>
              <DeliveryFeeAmount
                fee={deliveryFee}
                status={deliveryFeeStatus}
                onRetry={loadDeliveryFee}
              />
            </div>
          </div>
          <div className="bm-total">
            <span className="bm-total__label">Total</span>
            <span className="bm-total__value">
              {deliveryFee === null ? "—" : `LKR ${formatPrice(finalTotal)}`}
            </span>
          </div>
          {products.some((item) => item.nonCod) && (
            <div className="bm-note">
              <i className="ph-fill ph-info" />
              <span>
                Your cart has items that must be prepaid, so Cash on Delivery
                won't be available at checkout.
              </span>
            </div>
          )}
          <Link to="/checkout" className="btn btn-main w-100 rounded-8 mt-16 py-14 flex-center gap-8">
            Proceed to checkout
            <i className="ph-bold ph-arrow-right" />
          </Link>
          <div className="bm-continue">
            <Link to="/" className="bm-link">
              <i className="ph ph-arrow-left" />
              Continue shopping
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default CartSection;
